import { useEffect, useState } from "react";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../lib/firebase";
import styles from "./Preloader.module.css";

const LOGO_SRC = "/aya-tech.jpg";
const MIN_ANIM_MS = 2900; // Tiempo para ver trazado + aparición de nombre + palpitación + barra de carga
const MAX_WAIT_MS = 4800; // Tope máximo de seguridad

function preloadImage(url) {
  return new Promise((resolve) => {
    if (!url) return resolve();
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

const CAD_BIN_URL = "/aya-machine.bin?v=6";

function preloadCadBinary() {
  if (typeof window === "undefined") return Promise.resolve();
  if (!window.__AYA_MACHINE_PROMISE__ || window.__AYA_MACHINE_URL__ !== CAD_BIN_URL) {
    window.__AYA_MACHINE_URL__ = CAD_BIN_URL;
    window.__AYA_MACHINE_PROMISE__ = fetch(CAD_BIN_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .catch((err) => {
        window.__AYA_MACHINE_PROMISE__ = null;
        throw err;
      });
  }
  return window.__AYA_MACHINE_PROMISE__.catch(() => {});
}

export default function Preloader() {
  const [animDone, setAnimDone] = useState(false);
  const [assetsDone, setAssetsDone] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [hidden, setHidden] = useState(false);

  // 1. Ciclo completo de la animación (trazado -> nombre -> palpitación y carga)
  useEffect(() => {
    const t = setTimeout(() => setAnimDone(true), MIN_ANIM_MS);
    return () => clearTimeout(t);
  }, []);

  // 2. Precarga en segundo plano del modelo CAD 3D, imágenes de portada y fuentes
  useEffect(() => {
    let cancelled = false;

    const safetyTimeout = setTimeout(() => {
      if (!cancelled) setAssetsDone(true);
    }, MAX_WAIT_MS);

    (async () => {
      try {
        const tasks = [preloadImage(LOGO_SRC), preloadCadBinary()];

        const q = query(
          collection(db, "heroImages"),
          where("active", "==", true),
          orderBy("order")
        );
        const snap = await getDocs(q);
        const urls = snap.docs.map((d) => d.data()?.url).filter(Boolean);
        urls.slice(0, 2).forEach((u) => tasks.push(preloadImage(u)));

        if (document.fonts?.ready) {
          tasks.push(document.fonts.ready.catch(() => {}));
        }

        await Promise.all(tasks);
      } catch {
        // Continuar si algún recurso falla
      } finally {
        if (!cancelled) {
          clearTimeout(safetyTimeout);
          setAssetsDone(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(safetyTimeout);
    };
  }, []);

  // 3. Desvanecimiento suave cuando todo está listo
  useEffect(() => {
    if (animDone && assetsDone && !leaving) {
      setLeaving(true);
      const t = setTimeout(() => setHidden(true), 850);
      return () => clearTimeout(t);
    }
  }, [animDone, assetsDone, leaving]);

  if (hidden) return null;

  return (
    <div
      className={`${styles.overlay} ${leaving ? styles.leaving : ""}`}
      aria-label="Cargando Aya Technologies"
      role="status"
    >
      <div className={`${styles.stage} ${leaving ? styles.stageLeaving : ""}`}>
        {/* ── ISOTIPO CON TRAZADO CAD + HAZ VERDE + PALPITACIÓN ── */}
        <div className={styles.logoPulseWrap}>
          <span className={styles.pulseRing} aria-hidden="true" />
          <svg
            viewBox="0 300 1600 710"
            className={styles.svg}
            aria-hidden="true"
          >
            <defs>
              {/* Máscara 1: Monograma negro en trazo continuo */}
              <mask
                id="aya-monogram-mask"
                maskUnits="userSpaceOnUse"
                maskContentUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="1600"
                height="1542"
              >
                <rect x="0" y="0" width="1600" height="1542" fill="black" />
                <path
                  className={styles.monogramMaskPath}
                  d="M 795.5 696 L 566.5 390 L 184 945 L 628 945 L 1024.5 390 L 1406 945 L 962 945 Z"
                  fill="none"
                  stroke="white"
                  strokeWidth="195"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  pathLength="100"
                />
                <rect
                  className={styles.monogramFullFix}
                  x="0"
                  y="300"
                  width="1600"
                  height="710"
                  fill="white"
                />
                {/* Excluimos las 4 barras verdes y el texto original */}
                <rect x="30" y="688" width="165" height="54" fill="black" />
                <rect x="468" y="688" width="184" height="54" fill="black" />
                <rect x="938" y="688" width="184" height="54" fill="black" />
                <rect x="1396" y="688" width="146" height="54" fill="black" />
                <rect x="0" y="1010" width="1600" height="532" fill="black" />
              </mask>

              {/* Máscara 2: Los 4 segmentos verdes */}
              <clipPath id="aya-green-clip" clipPathUnits="userSpaceOnUse">
                <rect x="30" y="688" width="165" height="54" />
                <rect x="468" y="688" width="184" height="54" />
                <rect x="938" y="688" width="184" height="54" />
                <rect x="1396" y="688" width="146" height="54" />
              </clipPath>
              <mask
                id="aya-green-mask"
                maskUnits="userSpaceOnUse"
                maskContentUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="1600"
                height="1542"
              >
                <rect x="0" y="0" width="1600" height="1542" fill="black" />
                <rect
                  className={styles.greenMaskRect}
                  x="0"
                  y="675"
                  width="1600"
                  height="80"
                  fill="white"
                />
              </mask>
            </defs>

            {/* Línea guía CAD inicial */}
            <path
              className={styles.cadWireframe}
              d="M 795.5 696 L 566.5 390 L 184 945 L 628 945 L 1024.5 390 L 1406 945 L 962 945 Z"
              fill="none"
              pathLength="100"
            />

            {/* Monograma negro real */}
            <image
              href={LOGO_SRC}
              x="0"
              y="0"
              width="1600"
              height="1542"
              mask="url(#aya-monogram-mask)"
            />

            {/* 4 segmentos verdes reales */}
            <g clipPath="url(#aya-green-clip)" mask="url(#aya-green-mask)">
              <image
                href={LOGO_SRC}
                x="0"
                y="0"
                width="1600"
                height="1542"
              />
            </g>

            {/* Haz láser verde */}
            <line
              className={styles.laserBeam}
              x1="36"
              y1="715"
              x2="1536"
              y2="715"
              pathLength="100"
            />
          </svg>
        </div>

        {/* ── NOMBRE DE LA EMPRESA (aparece al terminar de formarse el logo) ── */}
        <div className={styles.brandBlock}>
          <div className={styles.brandName}>
            <span className={styles.wordAya}>AYA</span>
            <span className={styles.wordTech}>TECHNOLOGIES</span>
          </div>
          <span className={styles.brandSub}>// INGENIERÍA APLICADA</span>
        </div>

        {/* ── MOVIMIENTO DE CARGA (barra de progreso + pulso fluido) ── */}
        <div className={styles.loaderWrap} aria-hidden="true">
          <div className={styles.loaderTrack}>
            <div className={styles.loaderBar} />
          </div>
        </div>
      </div>
    </div>
  );
}
